import { auth, db } from "../firebase";
import { EForumTopic, ForumAuthor, ForumPost, ForumReply, FORUMS_LIKES_COLLECTION, FORUMS_POST_TIMESTAMP_FIELD, FORUMS_REPLY_PARENT_FIELD, FORUMS_REPLY_TIMESTAMP_FIELD, FORUMS_TOPIC_FIELD } from "../../types/db";
import { applyVariablesToQuery, QueryVariables } from "../db";
import { ForumPostService } from "../../types/services/forum/ForumService";
import { FirestoreQuery } from "../../types/db/common";
import { BaseFirestoreService } from "../../types/services/FirestoreService";

const forumService = new ForumPostService(db);

/** Get all the posts for a certain topic, if no topic provided get all */
export const getPostsList = async (topic?: EForumTopic, limit?: number) => {
    return forumService.list((collection) => {
        //limit query to past and present posts
        var query = collection.where(FORUMS_POST_TIMESTAMP_FIELD, '<=', Date.now());
        // var query: FirestoreQuery = collection;
        query = query.orderBy(FORUMS_POST_TIMESTAMP_FIELD, 'desc');
        if (!!topic) {
            query = query.where(FORUMS_TOPIC_FIELD, '==', topic);
        }
        if (!!limit) {
            query = query.limit(limit)
        }
        return query;
    })
}

/** Get all the posts from a certain author */
export const getUserPostsList = async (userId: string, limit?: number) => {
    return forumService.list((collection) => {
        //limit query to past and present posts
        var query = collection.where(FORUMS_POST_TIMESTAMP_FIELD, '<=', Date.now());
        // var query: FirestoreQuery = collection;
        query = query.orderBy(FORUMS_POST_TIMESTAMP_FIELD, 'desc');
        query = query.where('author.id', '==', userId);
        if (!!limit) {
            query = query.limit(limit)
        }
        return query;
    })
}

/** Get all the replies from a certain author */
export const getUserRepliesList = async (userId: string, limit?: number) => {
    var query: FirestoreQuery = forumService.getRepliesCollectionGroup();
    query = query.where(FORUMS_REPLY_TIMESTAMP_FIELD, '<=', Date.now());
    query = query.orderBy(FORUMS_REPLY_TIMESTAMP_FIELD, 'desc');
    query = query.where('author.id', '==', userId);
    if (!!limit) {
        query = query.limit(limit)
    }
    return BaseFirestoreService.querySnapshotToEntities<ForumReply>(await query.get());
}

/** Get all the likes from a certain author */
export const getUserLikesList = async (userId: string, limit?: number) => {
    var query: FirestoreQuery = db.collectionGroup(FORUMS_LIKES_COLLECTION);
    query = query.where('author.id', '==', userId);
    if (!!limit) {
        query = query.limit(limit)
    }
    return BaseFirestoreService.querySnapshotToEntities<ForumReply>(await query.get());
}

/** Get the information of a specific post */
export const getPostInfo = async (postId: string) => {
    return forumService.get(postId);
}

/** Get the reply for a specific post *///TODO add a limit and potential offset for pagination, and have deepness of fetch in replies
export const getPostReplies = async (postId: string, variables?: QueryVariables) => {
    return forumService.postReplyService(postId).list((collection) => {
        var query = applyVariablesToQuery(collection, variables);
        //only get the replies of the post itself
        query = query.where(FORUMS_REPLY_PARENT_FIELD, '==', null)
        return query
    })
}

/** Get the likes details for a specific post */ //TODO add a limit and potential offset for pagination
export const getPostLikesInfo = async (postId: string, variables?: QueryVariables) => {
    return forumService.postLikeService(postId).list((collection) => {
        var query = applyVariablesToQuery(collection, variables);
        return query
    })
}

/** Get the replies on a reply */ //TODO add a limit and potential offset for pagination, and have deepness of fetch in replies
export const getReplyReplies = async (postId: string, replyId: string, variables?: QueryVariables) => {
    return forumService.postReplyService(postId).list((collection) => {
        var query = applyVariablesToQuery(collection, variables);
        //only get the replies of the reply itself
        query = query.where(FORUMS_REPLY_PARENT_FIELD, '==', replyId)
        return query
    })
}

/** Create a new forum post in the DB */
export const createNewPost = (title: string, content: string, topic: EForumTopic) => {
    const user = auth.currentUser
    if (!user) {
        console.warn('trying to create a post for a non-logged user');
        return Promise.reject();
    }

    //rest of data filed by cloud function (to not have front end being able to change those)
    const data: Pick<ForumPost, 'author' | 'title' | 'content' | 'topic'> = {
        author: {
            id: user.uid,
            display_name: user.displayName || '',
            avatar_url: user.photoURL || ''
        },
        title,
        content,
        topic,
    }

    return forumService.create(data);
}

/** Create a new reply to a post in the DB */
export const addReplyToPost = (postId: string, content: string) => {
    const user = auth.currentUser
    if (!user) {
        console.warn('trying to add a reply for a non-logged user');
        return Promise.reject();
    }

    //rest of data filed by cloud function (to not have front end being able to change those)
    const data: Pick<ForumReply, 'author' | 'content'> = {
        author: {
            id: user.uid,
            display_name: user.displayName || '',
            avatar_url: user.photoURL || ''
        },
        content
    }

    return forumService.postReplyService(postId).create({ ...data, reply_to: null })
}

/** Create a new reply to a post in the DB */
export const addReplyToReply = (postId: string, replyId: string, content: string) => {
    const user = auth.currentUser
    if (!user) {
        console.warn('trying to add a reply for a non-logged user');
        return Promise.reject();
    }

    //rest of data filed by cloud function (to not have front end being able to change those)
    const data: Pick<ForumReply, 'author' | 'content'> = {
        author: {
            id: user.uid,
            display_name: user.displayName || '',
            avatar_url: user.photoURL || ''
        },
        content
    }

    return forumService.postReplyService(postId).create({ ...data, reply_to: replyId })
}

/** Edit a forum post in the DB */
export const editPost = (postId: string, title: string, content: string, topic: EForumTopic) => {
    const user = auth.currentUser
    if (!user) {
        console.warn('trying to edit a post for a non-logged user');
        return Promise.reject();
    }

    //Only Pick what can be changed front end (cannot change the author after post created)
    const data: Pick<ForumPost, 'title' | 'content' | 'topic'> = {
        title,
        content,
        topic,
    }

    return forumService.update(postId, data);
}

/** Edit a reply to a post/reply in the DB */
export const editReply = (postId: string, replyId: string, content: string) => {
    const user = auth.currentUser
    if (!user) {
        console.warn('trying to edit a reply for a non-logged user');
        return Promise.reject();
    }

    //Only Pick what can be changed front end (cannot change the author after post created)
    const data: Pick<ForumReply, 'content'> = {
        content
    }

    return forumService.postReplyService(postId).update(replyId, data)
}

export const deletePost = (postId: string) => {
    const user = auth.currentUser
    if (!user) {
        console.warn('trying to delete a post for a non-logged user');
        return Promise.reject();
    }
    return forumService.delete(postId);
}

export const deleteReply = (postId: string, replyId: string,) => {
    const user = auth.currentUser
    if (!user) {
        console.warn('trying to delete a reply for a non-logged user');
        return Promise.reject();
    }
    return forumService.postReplyService(postId).delete(replyId)
}

export const addLikeToPost = (postId: string) => {
    const user = auth.currentUser
    if (!user) {
        console.warn('trying to delete a reply for a non-logged user');
        return Promise.reject();
    }
    const author: ForumAuthor = {
        id: user.uid,
        display_name: user.displayName || '',
    }
    //Note: the duplication check is made by using the user id as doc id (done in the service)
    return forumService.postLikeService(postId).create({ author })
}

export const removeLikeFromPost = async (postId: string) => {
    const user = auth.currentUser
    if (!user) {
        console.warn('trying to delete a reply for a non-logged user');
        return Promise.reject();
    }

    return forumService.postLikeService(postId).delete(user.uid);
}

export const addLikeToReply = (postId: string, replyId: string) => {
    const user = auth.currentUser
    if (!user) {
        console.warn('trying to delete a reply for a non-logged user');
        return Promise.reject();
    }
    const author: ForumAuthor = {
        id: user.uid,
        display_name: user.displayName || '',
    }
    //Note: the duplication check is made by using the user id as doc id (done in the service)
    return forumService.postReplyService(postId).replyLikeService(replyId).create({ author });
}

export const removeLikeFromReply = async (postId: string, replyId: string) => {
    const user = auth.currentUser
    if (!user) {
        console.warn('trying to delete a reply for a non-logged user');
        return Promise.reject();
    }

    return forumService.postReplyService(postId).replyLikeService(replyId).delete(user.uid);
}

/** Return true if the post/reply is liked by the current user */
export const isLiked = async (postId: string, replyId?: string) => {
    const user = auth.currentUser
    if (!user) {
        //user not logged in, so cannot see history of likes
        return false;
    }

    if (!!replyId) {
        //like on reply
        return forumService.postReplyService(postId).replyLikeService(replyId).exists(user.uid)
    } else {
        //like on post
        return forumService.postLikeService(postId).exists(user.uid)
    }
}